import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { authStore } from "../../lib/authStore";
import { useI18n } from "../../lib/i18n";
import { AtelierModal } from "./AtelierModal";
import { PillCTA } from "./primitives";
import { analytics } from "../../lib/analytics";

const PURPOSES = [
  { key: "wedding", labelKey: "appointment.purpose.wedding" },
  { key: "business", labelKey: "appointment.purpose.business" },
  { key: "full_wardrobe", labelKey: "appointment.purpose.full_wardrobe" },
  { key: "other", labelKey: "appointment.purpose.other" },
];

/** M10 — book a private styling appointment. */
export function AppointmentModal({ open, onClose }) {
  const nav = useNavigate();
  const { t } = useI18n();
  const [date, setDate] = useState(""); // YYYY-MM-DD
  const [time, setTime] = useState("14:00");
  const [duration, setDuration] = useState(60);
  const [purpose, setPurpose] = useState("wedding");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    const token = authStore.getAccessToken();
    if (!token) { onClose?.(); nav("/auth/login?next=/account"); return; }
    if (!date || !time) { setError(t("appointment.error.datetime")); return; }
    // Build ISO in local time; server converts to UTC
    const iso = new Date(`${date}T${time}:00`).toISOString();
    setSubmitting(true);
    try {
      const r = await api.post("/me/appointments", {
        slot_start: iso,
        duration_minutes: duration,
        purpose,
        notes,
      });
      setDone(r.data);
      analytics.schedule(purpose);
    } catch (e) {
      setError(e?.response?.data?.detail || t("appointment.error.submit"));
    } finally { setSubmitting(false); }
  };

  const reset = () => { setDone(null); setNotes(""); };

  return (
    <AtelierModal
      open={open}
      onClose={() => { onClose?.(); setTimeout(reset, 400); }}
      title={t("appointment.title")}
      subtitle={t("appointment.subtitle")}
      testid="appointment-modal"
    >
      {!done ? (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-2 text-xs text-graphite/70">
            <span className="border-l-2 border-espresso bg-stone/25 px-3 py-2">{t("appointment.step.service")}</span>
            <span className="border-l-2 border-graphite/12 bg-paper px-3 py-2">{t("appointment.step.time")}</span>
            <span className="border-l-2 border-graphite/12 bg-paper px-3 py-2">{t("appointment.step.confirm")}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-graphite">{t("appointment.field.date")}</span>
              <input
                type="date"
                value={date}
                min={new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10)}
                onChange={(e) => setDate(e.target.value)}
                data-testid="apt-date"
                className="mt-2 min-h-11 w-full border border-graphite/18 bg-white px-3 text-sm text-graphite outline-none focus:border-espresso"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-graphite">{t("appointment.field.time")}</span>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="apt-time"
                className="mt-2 min-h-11 w-full border border-graphite/18 bg-white px-3 text-sm text-graphite outline-none focus:border-espresso"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-sm font-medium text-graphite">{t("appointment.field.duration")}</span>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              data-testid="apt-duration"
              className="mt-2 min-h-11 w-full border border-graphite/18 bg-white px-3 text-sm text-graphite outline-none focus:border-espresso"
            >
              <option value={45}>{t("appointment.duration.45")}</option>
              <option value={60}>{t("appointment.duration.60")}</option>
              <option value={90}>{t("appointment.duration.90")}</option>
              <option value={120}>{t("appointment.duration.120")}</option>
            </select>
          </label>
          <div>
            <span className="text-sm font-medium text-graphite">{t("appointment.field.purpose")}</span>
            <div className="flex flex-wrap gap-2 mt-3">
              {PURPOSES.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPurpose(p.key)}
                  data-testid={`apt-purpose-${p.key}`}
                  className={`min-h-11 border px-4 text-sm transition-colors ${
                    purpose === p.key ? "bg-graphite text-ivory border-graphite" : "border-graphite/30 hover:border-graphite"
                  }`}
                >
                  {t(p.labelKey)}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="text-sm font-medium text-graphite">{t("appointment.field.notes")}</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              data-testid="apt-notes"
              placeholder={t("appointment.field.notes_placeholder")}
              className="mt-2 w-full resize-none border border-graphite/18 bg-white px-3 py-3 text-sm text-graphite outline-none focus:border-espresso"
            />
          </label>
          {error && <div className="text-red-800/80 text-sm">{error}</div>}
          <PillCTA
            variant="primary"
            onClick={submit}
            disabled={submitting}
            data-testid="apt-submit"
            className="w-full rounded-none text-sm normal-case tracking-normal"
            style={{ textTransform: "none", letterSpacing: 0, borderRadius: 0 }}
          >
            {submitting ? t("appointment.requesting") : t("appointment.request")}
          </PillCTA>
        </div>
      ) : (
        <div data-testid="apt-result" className="space-y-4">
          <div className="border border-espresso/50 bg-stone/20 p-6">
            <span className="text-xs font-medium text-espresso">{t("appointment.requested")}</span>
            <p className="font-serif italic text-xl mt-3 leading-snug">
              {t("appointment.requested_body")}
            </p>
            <div className="mt-4 text-sm text-graphite/70">
              {new Date(done.slot_start).toLocaleString("az-AZ", { dateStyle: "long", timeStyle: "short" })}
            </div>
          </div>
        </div>
      )}
    </AtelierModal>
  );
}
